import { useState, useEffect } from 'preact/hooks';
import type { Connection } from '../../../types';
import { connectionManagementService } from '../../../services/connections/connection-management.service';
import { useScopeContext } from '../../../contexts/ScopeContext';

interface KnowledgeTabProps {
  connection: Connection;
  onConnectionUpdate: () => void;
}

export function KnowledgeTab({ connection }: KnowledgeTabProps) {
  const scope = useScopeContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [learningPrompt, setLearningPrompt] = useState('');
  const [knowledgeContext, setKnowledgeContext] = useState('');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editingContext, setEditingContext] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [contextDraft, setContextDraft] = useState('');

  useEffect(() => {
    loadKnowledge();
  }, [connection.connectionId || connection.id]);

  // Listen for knowledge base updates from WebSocket
  useEffect(() => {
    const handleKnowledgeUpdate = (event: CustomEvent) => {
      const { knowledgeBaseId } = event.detail;
      // knowledgeBaseId format: teamId:connectorId:connectionId
      const expectedId = `${scope.teamId}:${connection.connectorId!}:${connection.connectionId || connection.id}`;

      if (knowledgeBaseId === expectedId) {
        console.log('[KnowledgeTab] Received knowledge base update, refreshing...');
        loadKnowledge();
      }
    };

    window.addEventListener('knowledge-base-updated', handleKnowledgeUpdate as EventListener);
    return () => {
      window.removeEventListener('knowledge-base-updated', handleKnowledgeUpdate as EventListener);
    };
  }, [connection.connectionId || connection.id, connection.connectorId!, scope.teamId]);

  const loadKnowledge = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await connectionManagementService.getKnowledge(scope.teamId, connection.connectionId || connection.id);

      setLearningPrompt(data.learningPrompt || '');
      setKnowledgeContext(data.knowledgeContext || '');
      setLastUpdated(data.lastLearned || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load knowledge context');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await connectionManagementService.updateKnowledge(scope.teamId, connection.connectionId || connection.id, {
        learningPrompt: promptDraft
      });

      setLearningPrompt(promptDraft);
      setEditingPrompt(false);
      setSuccessMessage('Learning prompt updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update learning prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContext = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await connectionManagementService.updateKnowledge(scope.teamId, connection.connectionId || connection.id, {
        knowledgeContext: contextDraft
      });

      setKnowledgeContext(contextDraft);
      setEditingContext(false);
      setSuccessMessage('Knowledge context updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update knowledge context');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      setSuccessMessage(null);

      const result = await connectionManagementService.learnConnection(scope.teamId, connection.connectionId || connection.id);
      setSuccessMessage(`Learning job started: ${result.message || 'Processing...'}`);

      // Reload after a short delay to get updated data
      setTimeout(() => loadKnowledge(), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start learning');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3">Knowledge Context</h3>
        <div className="alert">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>Configure how the system learns about your workspace structure and data.</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Section 1: Learning Prompt */}
      <div className="card bg-base-200">
        <div className="card-body">
          <div className="flex justify-between items-center mb-2">
            <h4 className="card-title text-base">Learning Prompt</h4>
            {!editingPrompt && (
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setPromptDraft(learningPrompt);
                  setEditingPrompt(true);
                }}
              >
                Edit
              </button>
            )}
          </div>

          {editingPrompt ? (
            <div className="space-y-3">
              <textarea
                className="textarea textarea-bordered w-full h-32"
                value={promptDraft}
                onChange={(e) => setPromptDraft((e.target as HTMLTextAreaElement).value)}
                placeholder="Enter learning prompt..."
              />
              <div className="flex gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSavePrompt}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditingPrompt(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{learningPrompt || 'No learning prompt set'}</p>
          )}
        </div>
      </div>

      {/* Section 2: Knowledge Context Display */}
      <div className="card bg-base-200">
        <div className="card-body">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <h4 className="card-title text-base">Compiled Knowledge Context</h4>
            </div>
            <div className="flex gap-2">
              {!editingContext && knowledgeContext && (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setContextDraft(knowledgeContext);
                    setEditingContext(true);
                  }}
                >
                  Edit
                </button>
              )}
              <button
                className="btn btn-sm btn-primary"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    Refreshing...
                  </>
                ) : (
                  'Refresh'
                )}
              </button>
            </div>
          </div>

          {lastUpdated && (
            <p className="text-xs opacity-70 mb-2">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}

          {editingContext ? (
            <div className="space-y-3">
              <textarea
                className="textarea textarea-bordered w-full h-64 font-mono text-sm"
                value={contextDraft}
                onChange={(e) => setContextDraft((e.target as HTMLTextAreaElement).value)}
                placeholder="Knowledge context..."
              />
              <div className="flex gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveContext}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditingContext(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-base-300 p-4 rounded max-h-96 overflow-auto">
              {knowledgeContext ? (
                <pre className="text-sm whitespace-pre-wrap">{knowledgeContext}</pre>
              ) : (
                <p className="text-sm opacity-70">No knowledge context available. Click "Refresh" to generate.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
